import React, { useCallback, useRef, useState } from 'react';
import { FileSpreadsheet, Upload, X } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import { cn } from '../../lib/utils';

const defaultCsvValidator = (file) => {
  const name = file.name.toLowerCase();
  return (
    file.type === 'text/csv' ||
    file.type === 'application/vnd.ms-excel' ||
    name.endsWith('.csv')
  );
};

const spreadsheetValidator = (file) => {
  const name = file.name.toLowerCase();
  return (
    defaultCsvValidator(file) ||
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    name.endsWith('.xlsx') ||
    name.endsWith('.xls')
  );
};

function CsvDropZone({
  disabled,
  fileName,
  onFileSelect,
  onClear,
  className,
  accept = '.csv,text/csv',
  validateFile = defaultCsvValidator,
  titleKey = 'tenantCsvDropTitle',
  hintKey = 'tenantCsvDropHint',
  activeKey = 'tenantCsvDropActive',
  hasFileKey = 'tenantCsvDropHasFile',
  clearLabelKey = 'tenantCsvDropClear',
}) {
  const { t } = useLocale();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const acceptFile = useCallback(
    (file) => {
      if (!file || !validateFile(file)) return;
      onFileSelect?.(file);
    },
    [onFileSelect, validateFile]
  );

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    if (disabled) return;
    acceptFile(event.dataTransfer?.files?.[0]);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!disabled) setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openPicker();
        }
      }}
      onClick={openPicker}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={cn(
        'relative flex min-h-[7rem] w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors',
        dragOver && 'border-primary-500 bg-primary-50',
        !dragOver && !fileName && 'border-border-light bg-background-card hover:border-primary-300 hover:bg-primary-50/40',
        fileName && 'border-success-300 bg-success-50/30',
        disabled && 'pointer-events-none opacity-60',
        className
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        disabled={disabled}
        onChange={(e) => {
          acceptFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />

      {fileName ? (
        <>
          <FileSpreadsheet className="h-8 w-8 text-success-600" aria-hidden />
          <p className="max-w-full truncate text-sm font-medium text-text-primary">{fileName}</p>
          <p className="text-xs text-text-secondary">{t(hasFileKey)}</p>
          {onClear ? (
            <button
              type="button"
              className="absolute right-2 top-2 rounded-md p-1 text-text-tertiary hover:bg-background-tertiary hover:text-text-primary"
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              aria-label={t(clearLabelKey)}
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </>
      ) : (
        <>
          <Upload
            className={cn('h-8 w-8', dragOver ? 'text-primary-600' : 'text-text-tertiary')}
            aria-hidden
          />
          <p className="text-sm font-medium text-text-primary">
            {dragOver ? t(activeKey) : t(titleKey)}
          </p>
          <p className="text-xs text-text-secondary">{t(hintKey)}</p>
        </>
      )}
    </div>
  );
}

export { defaultCsvValidator, spreadsheetValidator };
export default CsvDropZone;
